import React, { useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'
import { useRevealProps } from '../../MwPage/components/Reveal'
import { IDS } from '../../MwPage/content'
import {
  blue,
  blue2,
  cyan,
  Eyebrow,
  Hi,
  ice,
  iceLine,
  ink,
  Lede,
  line,
  muted,
  paper,
  sans,
  Section,
  SectionHead,
  Stack,
  teal,
  ui,
} from '../../MwPage/styled'
import { useFocus } from '../focus'
import { useScoreEvent } from '../metaScore'
import { Bullets, Card, Column, Grid, H2, Kicker, Tag, Tags, Wrap } from '../styled'
import { assessTrial } from '../trialScorer'

// the prototype's .scorer: a white card of label / slider / value rows
const Sliders = styled(Card)`
  display: grid;
  gap: 4px;
  padding: 14px 18px;
`

const Row = styled.div`
  display: grid;
  grid-template-columns: minmax(120px, 1fr) 2fr 36px;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
  border-top: 1px solid ${line};

  &:first-child {
    border-top: 0;
  }

  label {
    font: 600 15px/1.3 ${sans};
    color: ${ink};
  }

  output {
    text-align: right;
    font: 800 16px/1 ${ui};
    color: ${teal};
  }

  @media (max-width: 520px) {
    grid-template-columns: 1fr 36px;

    label {
      grid-column: 1 / -1;
    }
  }
`

// the slider filled to the thumb; --p is the fill, set inline from the score
const Slider = styled.input`
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 28px;
  margin: 0;
  background: transparent;
  cursor: pointer;

  &::-webkit-slider-runnable-track {
    height: 8px;
    border-radius: 999px;
    background: linear-gradient(to right, ${blue2} var(--p), ${iceLine} var(--p));
  }

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    box-sizing: border-box;
    width: 22px;
    height: 22px;
    margin-top: -7px;
    border-radius: 50%;
    background: ${paper};
    border: 4px solid ${blue};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
  }

  &::-moz-range-track {
    height: 8px;
    border-radius: 999px;
    background: ${iceLine};
  }

  &::-moz-range-progress {
    height: 8px;
    border-radius: 999px;
    background: ${blue2};
  }

  &::-moz-range-thumb {
    box-sizing: border-box;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: ${paper};
    border: 4px solid ${blue};
  }

  &:focus-visible {
    outline: 2px solid ${cyan};
    outline-offset: 2px;
    border-radius: 999px;
  }
`

const Tools = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  font-size: 13px;
  color: ${muted};
`

const Reset = styled.button`
  flex: none;
  padding: 6px 14px;
  border-radius: 999px;
  border: 1.5px solid ${iceLine};
  background: ${paper};
  color: ${teal};
  font: 600 13px/1.3 ${sans};
  cursor: pointer;

  &:hover {
    border-color: ${cyan};
  }
`

// the prototype's .score: the percentage beside the stage and the focus chips
const ScoreCard = styled(Card)`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 16px 24px;
  align-items: center;
  background: ${ice};

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`

const Percent = styled.div`
  font: 800 44px/1 ${ui};
  letter-spacing: -0.04em;
  color: ${teal};

  small {
    display: block;
    margin-top: 6px;
    font: 600 13px/1.3 ${sans};
    letter-spacing: 0;
    color: ${muted};
  }
`

const StageName = styled.div`
  margin-bottom: 10px;
  font: 800 22px/1.2 ${sans};
  letter-spacing: -0.02em;
  color: ${ink};
`

const Outcome = styled(Card)`
  h3 {
    margin: 0 0 8px;
    font-size: 19px;
  }

  p {
    margin: 0 0 12px;
  }
`

const SliderRow = ({ name, score, index, onChange }) => {
  const { scorer } = useFocus()
  const fill = `calc(11px + (100% - 22px) * ${(score - 1) / 4})`
  const id = `trial-score-${index}`
  return (
    <Row>
      <label htmlFor={id}>{name}</label>
      <Slider
        id={id}
        type="range"
        min="1"
        max="5"
        step="1"
        value={score}
        aria-label={scorer.sliderLabel(name)}
        onChange={(event) => onChange(index, Number(event.target.value))}
        style={{ '--p': fill }}
      />
      <output htmlFor={id}>{score}</output>
    </Row>
  )
}

/**
 * "Free assessment": the prototype's seven sliders, the score they add up to, the stage it places the visitor on
 * and the two outcomes (see ../trialScorer.js). The result reaches Meta once the sliders settle.
 */
const TrialScorerSection = () => {
  const { scorer, key: page } = useFocus()
  const [scores, setScores] = useState(scorer.start)
  // the example scores are the page's, not the visitor's: nothing is reported until a slider moves
  const [touched, setTouched] = useState(false)
  const set = useCallback((index, score) => {
    setTouched(true)
    setScores((current) => current.map((value, i) => (i === index ? score : value)))
  }, [])
  const reset = useCallback(() => setScores(scorer.start), [scorer.start])
  const result = useMemo(() => assessTrial(scorer, scores), [scorer, scores])
  useScoreEvent({ result: { stage: result.stage.name, rung: result.rung, pct: result.pct }, touched, page })

  return (
    <Section id={IDS.where}>
      <Wrap>
        <Stack $gap="clamp(20px, 2.6vw, 26px)">
          <SectionHead>
            <Eyebrow {...useRevealProps()}>{scorer.eyebrow}</Eyebrow>
            <H2 {...useRevealProps()}>
              {scorer.headline} <Hi>{scorer.headlineAccent}</Hi>
            </H2>
            <Lede {...useRevealProps()}>{scorer.lede}</Lede>
          </SectionHead>
          <Column $max={820} $gap={14}>
            <Sliders {...useRevealProps()}>
              {scorer.areas.map((name, index) => (
                <SliderRow key={name} name={name} index={index} score={scores[index]} onChange={set} />
              ))}
            </Sliders>
            <Tools>
              <span>{scorer.example}</span>
              <Reset type="button" onClick={reset}>
                {scorer.reset}
              </Reset>
            </Tools>
            <ScoreCard aria-live="polite">
              <Percent>
                {result.pct}%<small>{scorer.points(result.sum, result.total)}</small>
              </Percent>
              <div>
                <Kicker>{scorer.stageLabel}</Kicker>
                <StageName>{result.stage.name}</StageName>
                <Kicker>{scorer.focusLabel}</Kicker>
                <Tags role="list">
                  {(result.focus.length ? result.focus : [scorer.noFocus]).map((name) => (
                    <Tag key={name} role="listitem">
                      {name}
                    </Tag>
                  ))}
                </Tags>
              </div>
            </ScoreCard>
            <Grid $min={300}>
              <Outcome>
                <Kicker>{scorer.outcomes[0]}</Kicker>
                <h3>{result.stage.name}</h3>
                <p>{result.stage.intro}</p>
                <Bullets>
                  {result.stage.bullets.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </Bullets>
              </Outcome>
              <Outcome>
                <Kicker>{scorer.outcomes[1]}</Kicker>
                <h3>
                  {result.lowest.name} ({result.lowest.score}/5)
                </h3>
                <p>
                  {result.lowest.score >= 4 && scorer.sharpen}
                  {result.lowest.intro}
                </p>
                <Bullets>
                  {result.lowest.bullets.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </Bullets>
              </Outcome>
            </Grid>
          </Column>
        </Stack>
      </Wrap>
    </Section>
  )
}

export default TrialScorerSection
